<template>
  <div class="home">
    <section class="hero">
      <div class="hero-grid"></div>
      <div class="hero-shell">
        <div class="hero-copy">
          <p class="eyebrow">
            <span></span>
            One-on-one online accounting tutoring
          </p>
          <h1>
            Accounting,
            <em>decoded.</em>
          </h1>
          <p class="hero-text">
            Patient, step-by-step tutoring that turns confusing lectures,
            assignments, and exam reviews into ideas you can actually use.
          </p>
          <div class="hero-actions">
            <router-link class="primary-button" to="/contact">
              Book a free consultation
              <i class="fas fa-arrow-right" aria-hidden="true"></i>
            </router-link>
            <router-link class="text-button" to="/howitworks">
              See the tutoring process
            </router-link>
          </div>
          <div class="hero-proof">
            <span><i class="fas fa-circle-check" aria-hidden="true"></i> College accounting</span>
            <span><i class="fas fa-circle-check" aria-hidden="true"></i> Flexible online sessions</span>
            <span><i class="fas fa-circle-check" aria-hidden="true"></i> Judgment-free questions</span>
          </div>
        </div>

        <div class="portrait-stage">
          <div class="portrait-orbit" aria-hidden="true"></div>
          <div class="portrait-frame">
            <img
              src="../../assets/profile2.png"
              alt="Ali Khalil, online accounting tutor"
              width="700"
              height="700"
              loading="eager"
              fetchpriority="high"
            />
          </div>
          <div class="portrait-label">
            <span>Former peer tutor</span>
            <strong>SUNY Ulster</strong>
          </div>
          <div class="portrait-note">
            <strong>1:1</strong>
            <span>Focused entirely on you</span>
          </div>
        </div>
      </div>
    </section>

    <section class="signal-strip" aria-label="Tutoring highlights">
      <div>
        <strong>30 min</strong>
        <span>Free consultation</span>
      </div>
      <div>
        <strong>$35</strong>
        <span>Single-session rate</span>
      </div>
      <div>
        <strong>Online</strong>
        <span>Learn from wherever you focus best</span>
      </div>
    </section>

    <section class="statement">
      <p class="section-kicker">A different kind of tutoring</p>
      <div class="statement-grid">
        <h2>
          You don’t need more accounting jargon.
          <span>You need the right explanation.</span>
        </h2>
        <div>
          <p>
            When a class moves too fast, one missed step can make everything
            after it feel impossible. We find that step, slow it down, and build
            forward together.
          </p>
          <p>
            Bring your syllabus, homework, review sheet, or the exact question
            that is frustrating you. Every session starts where you actually are.
          </p>
        </div>
      </div>
    </section>

    <section id="services" class="services">
      <div class="section-heading">
        <p class="section-kicker">Built around your course</p>
        <h2>Focused help for the moments that matter.</h2>
      </div>

      <div class="service-grid">
        <article class="service-card featured">
          <span class="card-number">01</span>
          <div class="service-icon"><i class="fas fa-pen-ruler" aria-hidden="true"></i></div>
          <h3>Concept Clarity</h3>
          <p>
            Understand debits, credits, financial statements, reconciliations,
            and other core topics through plain-language explanations.
          </p>
        </article>
        <article class="service-card">
          <span class="card-number">02</span>
          <div class="service-icon"><i class="fas fa-list-check" aria-hidden="true"></i></div>
          <h3>Exam Preparation</h3>
          <p>
            Turn your review materials into a realistic study plan and practice
            the problems most relevant to your course.
          </p>
        </article>
        <article class="service-card">
          <span class="card-number">03</span>
          <div class="service-icon"><i class="fas fa-laptop" aria-hidden="true"></i></div>
          <h3>Homework Support</h3>
          <p>
            Work through the reasoning behind each step so you can complete
            similar problems independently.
          </p>
        </article>
      </div>
    </section>

    <section id="pricing" class="pricing">
      <div class="pricing-shell">
        <div class="pricing-intro">
          <p class="section-kicker light">Simple, transparent pricing</p>
          <h2>Choose the pace that fits your semester.</h2>
          <p>
            No hidden fees. Start with one session or save with a focused package.
          </p>
        </div>

        <div class="pricing-grid">
          <article class="price-card">
            <p class="plan-label">Single session</p>
            <p class="price"><strong>$35</strong><span>/ hour</span></p>
            <ul>
              <li>One focused hour</li>
              <li>Flexible scheduling</li>
              <li>Pay as you go</li>
            </ul>
            <router-link to="/contact">Book consultation</router-link>
          </article>

          <article class="price-card accent">
            <p class="popular">Most support</p>
            <p class="plan-label">Mastery series</p>
            <p class="price"><strong>$150</strong><span>5 sessions</span></p>
            <ul>
              <li>Five one-hour sessions</li>
              <li>Save $25</li>
              <li>Flexible rescheduling</li>
            </ul>
            <router-link to="/contact">Book consultation</router-link>
          </article>

          <article class="price-card">
            <p class="plan-label">Progress accelerator</p>
            <p class="price"><strong>$80</strong><span>3 sessions</span></p>
            <ul>
              <li>Three one-hour sessions</li>
              <li>Save $25</li>
              <li>Priority scheduling</li>
            </ul>
            <router-link to="/contact">Book consultation</router-link>
          </article>
        </div>
      </div>
    </section>

    <section id="testimonials" class="testimonials">
      <div class="section-heading">
        <p class="section-kicker">Student perspective</p>
        <h2>Clarity changes everything.</h2>
      </div>
      <div class="testimonial-grid">
        <blockquote>
          <i class="fas fa-quote-left" aria-hidden="true"></i>
          <p>
            “Ali took challenging topics like financial statements and
            reconciliations and explained them in a way that made sense to me.
            His patience and enthusiasm kept me motivated.”
          </p>
          <footer>
            <strong>Monique Washington</strong>
            <span>SUNY Ulster Student</span>
          </footer>
        </blockquote>
        <blockquote>
          <i class="fas fa-quote-left" aria-hidden="true"></i>
          <p>
            “He has a remarkable ability to break down complex topics into
            clear, logical steps. Accounting transformed from a daunting
            subject into one I genuinely enjoy.”
          </p>
          <footer>
            <strong>Tevin Williams</strong>
            <span>Accounting Student</span>
          </footer>
        </blockquote>
      </div>
    </section>

    <section class="closing-cta">
      <div>
        <p class="section-kicker light">Your next step is simple</p>
        <h2>Bring the confusion. Leave with a plan.</h2>
      </div>
      <router-link to="/contact">
        Find a time that works
        <i class="fas fa-arrow-right" aria-hidden="true"></i>
      </router-link>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.home {
  background: var(--paper);
}

.hero {
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 78px);
  background: var(--ink);
  color: #fff;
}

.hero-grid {
  position: absolute;
  inset: 0;
  opacity: 0.16;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to right, #000, transparent 82%);
}

.hero-shell {
  position: relative;
  z-index: 1;
  display: grid;
  width: min(1240px, calc(100% - 40px));
  min-height: calc(100vh - 78px);
  margin: 0 auto;
  grid-template-columns: 1.05fr 0.95fr;
  gap: clamp(3rem, 7vw, 7rem);
  align-items: center;
  padding: 4rem 0;
}

.eyebrow,
.section-kicker {
  margin: 0 0 1.25rem;
  color: var(--blue);
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  color: #aebcff;

  span {
    width: 28px;
    height: 2px;
    border-radius: 99px;
    background: var(--amber);
  }
}

.hero-copy h1 {
  max-width: 680px;
  margin: 0;
  font-size: clamp(4.3rem, 9.3vw, 8.8rem);
  letter-spacing: -0.085em;
  line-height: 0.82;

  em {
    display: block;
    color: var(--amber);
    font-style: normal;
  }
}

.hero-text {
  max-width: 620px;
  margin: 2rem 0 0;
  color: #b9c3d8;
  font-size: clamp(1rem, 1.4vw, 1.22rem);
  line-height: 1.75;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  margin-top: 2rem;
}

.primary-button,
.text-button {
  display: inline-flex;
  min-height: 52px;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  font-size: 0.88rem;
  font-weight: 900;
}

.primary-button {
  gap: 0.7rem;
  padding: 0.9rem 1.2rem;
  background: var(--blue);
  color: #fff;
  box-shadow: 0 16px 40px rgba(49, 87, 246, 0.35);
  transition: all 180ms ease;

  &:hover {
    background: #4167ff;
    transform: translateY(-3px);
  }
}

.text-button {
  padding: 0.9rem 0.5rem;
  color: #dce3f2;
  text-decoration: underline;
  text-decoration-color: #52607a;
  text-underline-offset: 6px;

  &:hover {
    color: #fff;
    text-decoration-color: var(--amber);
  }
}

.hero-proof {
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem 1.4rem;
  margin-top: 2rem;
  color: #8f9bb3;
  font-size: 0.74rem;
  font-weight: 700;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  i {
    color: var(--mint);
  }
}

.portrait-stage {
  position: relative;
  min-height: 620px;
}

.portrait-orbit {
  position: absolute;
  top: 8%;
  right: -8%;
  width: 82%;
  aspect-ratio: 1;
  border: 1px solid rgba(158, 177, 255, 0.3);
  border-radius: 50%;
}

.portrait-frame {
  position: absolute;
  inset: 8% 7% 4% 5%;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 48% 48% 24px 24px;
  background:
    radial-gradient(circle at 50% 20%, rgba(98, 128, 255, 0.9), transparent 42%),
    linear-gradient(145deg, #20345e, #0e172d);
  box-shadow: 0 36px 100px rgba(0, 0, 0, 0.42);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
    filter: saturate(0.96) contrast(1.02);
  }
}

.portrait-label,
.portrait-note {
  position: absolute;
  z-index: 2;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.26);
  backdrop-filter: blur(16px);
}

.portrait-label {
  top: 8%;
  left: -2%;
  padding: 0.85rem 1rem;
  border-radius: 16px;
  background: rgba(10, 16, 32, 0.75);

  span,
  strong {
    display: block;
  }

  span {
    color: #8f9bb3;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  strong {
    margin-top: 0.2rem;
    font-size: 0.94rem;
  }
}

.portrait-note {
  right: -1%;
  bottom: 5%;
  display: flex;
  width: 190px;
  gap: 0.75rem;
  padding: 1rem;
  align-items: center;
  border-radius: 18px;
  background: rgba(255, 189, 89, 0.94);
  color: var(--ink);

  strong {
    font-size: 1.5rem;
  }

  span {
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1.25;
  }
}

.signal-strip {
  display: grid;
  width: min(1180px, calc(100% - 40px));
  margin: -1.5rem auto 0;
  position: relative;
  z-index: 4;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid var(--line);
  border-radius: 22px;
  background: #fff;
  box-shadow: var(--shadow);

  div {
    padding: 1.4rem 2rem;

    & + div {
      border-left: 1px solid var(--line);
    }
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: var(--ink);
    font-size: 1.45rem;
    letter-spacing: -0.04em;
  }

  span {
    margin-top: 0.25rem;
    color: var(--muted);
    font-size: 0.78rem;
    font-weight: 600;
  }
}

.statement,
.services,
.testimonials {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: clamp(5rem, 10vw, 9rem) 0;
}

.statement-grid {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: clamp(3rem, 8vw, 8rem);
  align-items: start;

  h2 {
    margin: 0;
    font-size: clamp(2.5rem, 5vw, 5rem);
    letter-spacing: -0.065em;
    line-height: 1;

    span {
      display: block;
      color: var(--blue);
    }
  }

  p {
    margin: 0 0 1.2rem;
    color: var(--muted);
    line-height: 1.8;
  }
}

.services {
  padding-top: 2rem;
}

.section-heading {
  max-width: 780px;
  margin-bottom: 3rem;

  h2 {
    margin: 0;
    font-size: clamp(2.6rem, 5.5vw, 5.2rem);
    letter-spacing: -0.07em;
    line-height: 1;
  }
}

.service-grid {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr 0.85fr;
  gap: 1rem;
}

.service-card {
  position: relative;
  min-height: 360px;
  overflow: hidden;
  padding: 2rem;
  border: 1px solid var(--line);
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 14px 40px rgba(10, 16, 32, 0.06);
  transition: transform 220ms ease, box-shadow 220ms ease;

  &:hover {
    box-shadow: var(--shadow);
    transform: translateY(-6px);
  }

  &.featured {
    background: var(--blue);
    color: #fff;

    p {
      color: #cfdaff;
    }

    .card-number {
      color: rgba(255, 255, 255, 0.36);
    }

    .service-icon {
      background: var(--amber);
      color: var(--ink);
    }
  }

  h3 {
    margin: 3.7rem 0 0.8rem;
    font-size: 1.55rem;
    letter-spacing: -0.04em;
  }

  p {
    margin: 0;
    color: var(--muted);
    font-size: 0.92rem;
    line-height: 1.75;
  }
}

.card-number {
  position: absolute;
  top: 1.7rem;
  right: 1.8rem;
  color: #c4cad6;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.12em;
}

.service-icon {
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  border-radius: 17px;
  background: #edf1ff;
  color: var(--blue);
  font-size: 1.15rem;
}

.pricing {
  padding: 1.25rem;
}

.pricing-shell {
  width: min(1240px, 100%);
  margin: 0 auto;
  padding: clamp(3rem, 7vw, 6rem);
  border-radius: 32px;
  background:
    radial-gradient(circle at 10% 0%, rgba(49, 87, 246, 0.38), transparent 30%),
    var(--ink);
  color: #fff;
}

.section-kicker.light {
  color: #9eb1ff;
}

.pricing-intro {
  display: grid;
  margin-bottom: 3rem;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2rem;
  align-items: end;

  .section-kicker {
    grid-column: 1 / -1;
    margin-bottom: -0.6rem;
  }

  h2 {
    max-width: 700px;
    margin: 0;
    font-size: clamp(2.5rem, 5vw, 4.8rem);
    letter-spacing: -0.07em;
    line-height: 1;
  }

  > p:last-child {
    max-width: 430px;
    margin: 0;
    color: #9ca8be;
    line-height: 1.75;
  }
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.price-card {
  position: relative;
  padding: 1.8rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.06);

  &.accent {
    border-color: var(--amber);
    background: #fff;
    color: var(--ink);

    ul {
      color: var(--muted);
    }

    a {
      background: var(--blue);
      color: #fff;
    }
  }

  .plan-label {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 900;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .price {
    display: flex;
    margin: 2.2rem 0 1.5rem;
    align-items: end;
    gap: 0.65rem;

    strong {
      font-size: clamp(2.7rem, 5vw, 4.2rem);
      letter-spacing: -0.07em;
      line-height: 0.85;
    }

    span {
      color: #8e99af;
      font-size: 0.76rem;
      font-weight: 700;
    }
  }

  ul {
    min-height: 125px;
    margin: 0;
    padding: 0;
    color: #b4bdd0;
    list-style: none;

    li {
      margin: 0.7rem 0;
      font-size: 0.84rem;

      &::before {
        margin-right: 0.55rem;
        color: var(--mint);
        content: "✓";
        font-weight: 900;
      }
    }
  }

  a {
    display: block;
    margin-top: 1rem;
    padding: 0.9rem;
    border-radius: 12px;
    background: #fff;
    color: var(--ink);
    font-size: 0.82rem;
    font-weight: 900;
    text-align: center;
  }
}

.popular {
  position: absolute;
  top: -0.75rem;
  right: 1rem;
  margin: 0;
  padding: 0.42rem 0.7rem;
  border-radius: 99px;
  background: var(--amber);
  color: var(--ink);
  font-size: 0.62rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.testimonial-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

blockquote {
  margin: 0;
  padding: clamp(2rem, 4vw, 3.2rem);
  border: 1px solid var(--line);
  border-radius: 24px;
  background: #fff;

  > i {
    color: var(--blue);
    font-size: 1.35rem;
  }

  > p {
    min-height: 170px;
    margin: 1.6rem 0 2rem;
    color: var(--ink-soft);
    font-size: clamp(1.08rem, 1.7vw, 1.4rem);
    font-weight: 600;
    line-height: 1.65;
  }

  footer {
    display: flex;
    flex-direction: column;

    strong {
      font-size: 0.88rem;
    }

    span {
      margin-top: 0.2rem;
      color: var(--muted);
      font-size: 0.75rem;
    }
  }
}

.closing-cta {
  display: grid;
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto 7rem;
  padding: clamp(2.4rem, 6vw, 5rem);
  grid-template-columns: 1fr auto;
  gap: 2rem;
  align-items: end;
  border-radius: 28px;
  background: var(--blue);
  color: #fff;

  h2 {
    max-width: 720px;
    margin: 0;
    font-size: clamp(2.3rem, 5vw, 4.8rem);
    letter-spacing: -0.07em;
    line-height: 1;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.7rem;
    padding: 1rem 1.2rem;
    border-radius: 14px;
    background: #fff;
    color: var(--ink);
    font-size: 0.85rem;
    font-weight: 900;
    white-space: nowrap;
  }
}

@media (max-width: 980px) {
  .hero-shell {
    grid-template-columns: 1fr;
    padding-top: 6rem;
  }

  .hero {
    min-height: auto;
  }

  .hero-shell {
    min-height: auto;
  }

  .portrait-stage {
    min-height: 620px;
    width: min(620px, 100%);
    margin: 0 auto;
  }

  .statement-grid,
  .pricing-intro {
    grid-template-columns: 1fr;
  }

  .service-grid,
  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .service-card {
    min-height: 300px;
  }

  .price-card ul {
    min-height: auto;
  }
}

@media (max-width: 720px) {
  .hero-shell {
    width: min(100% - 28px, 1240px);
    min-width: 0;
    grid-template-columns: minmax(0, 1fr);
    padding: 4.5rem 0 2rem;
  }

  .hero-copy,
  .portrait-stage {
    width: 100%;
    min-width: 0;
  }

  .eyebrow {
    align-items: flex-start;
    gap: 0.55rem;
    font-size: 0.66rem;
    letter-spacing: 0.09em;
    line-height: 1.5;

    span {
      flex: 0 0 24px;
      margin-top: 0.55em;
    }
  }

  .hero-copy h1 {
    max-width: 100%;
    font-size: clamp(2.9rem, 15vw, 4rem);
    letter-spacing: -0.075em;
    line-height: 0.88;
  }

  .hero-text {
    max-width: 100%;
    font-size: 0.96rem;
    line-height: 1.7;
  }

  .hero-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .primary-button,
  .text-button {
    width: 100%;
  }

  .portrait-stage {
    min-height: min(116vw, 470px);
  }

  .portrait-frame {
    inset: 6% 0 5%;
  }

  .portrait-label {
    top: 6%;
    left: 0;
    max-width: 58%;
    padding: 0.75rem 0.85rem;

    span {
      font-size: 0.56rem;
      line-height: 1.4;
    }

    strong {
      font-size: 0.84rem;
    }
  }

  .portrait-note {
    right: 0;
    bottom: 4%;
    width: 154px;
    max-width: 47%;
    gap: 0.55rem;
    padding: 0.8rem;

    strong {
      font-size: 1.25rem;
    }

    span {
      font-size: 0.63rem;
    }
  }

  .hero-proof {
    gap: 0.75rem 1rem;
  }

  .signal-strip {
    grid-template-columns: 1fr;
    margin-top: 1rem;

    div + div {
      border-top: 1px solid var(--line);
      border-left: 0;
    }
  }

  .statement,
  .services,
  .testimonials {
    width: min(100% - 28px, 1180px);
  }

  .testimonial-grid,
  .closing-cta {
    grid-template-columns: 1fr;
  }

  blockquote > p {
    min-height: auto;
  }

  .pricing {
    padding: 0.65rem;
  }

  .pricing-shell {
    padding: 3rem 1rem;
    border-radius: 24px;
  }

  .closing-cta {
    width: min(100% - 28px, 1180px);
    margin-bottom: 4rem;
  }
}
</style>
